import {
	Breakpoints,
	Checkbox,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Spinner,
	Text,
	TextTag,
	TokenTextAppearance,
	TextInput,
	ThemeProvider,
} from '@volkswagen-onehub/components-core';
import { LayerCloseHandleV2 } from '@volkswagen-onehub/layer-manager';
import i18next from 'i18next';
import React, { Suspense, useEffect, useState } from 'react';
import './ShareVehicle.scss';
import { HistoryServiceV3 } from '@feature-hub/history-service';
import { Modal } from '../../models/Modal';
import { ShareUrl } from '../../models/ShareUrl';
import { DataRequestState } from '../../models/DataRequestState';
import { VehicleObject } from '../../models/Vehicle';
import { TechnicalDataObject } from '../../models/TechnicalData';

function ShareVehicle({ state }: Modal<ShareVehicleProps>) {
	const { vin, historyService, getShareUrl, authorized, trackingService, isLoggedIn, vehicle, technicalData, t } = state;

	const [additionalContent, setAdditionalContent] = useState(false);
	const [, setSuccess] = useState(0);
	const [shareUrl, setShareUrl] = useState({ urlAuthorized: '', urlNotAuthorized: '' } as ShareUrl);
	const [tempIsLoading, setTempIsLoading] = useState(true);

	const printJsonAsPlainText = (input: any, indent = '') => {
		// Iterate through each key-value pair in the JSON object
		let output: string = '';
		// eslint-disable-next-line no-restricted-syntax
		for (const key in input) {
			if (
				key === 'includesExtendedData' ||
				key === 'preFilledAuthData' ||
				key === 'includesTechnicalDetails' ||
				key === 'efficiencyInformation' ||
				key === 'productBrand' ||
				key === 'media'
			) {
				// eslint-disable-next-line no-continue
				continue;
			}
			const value = input[key];

			// Handling different types of values
			if (Array.isArray(value)) {
				// For arrays, print key as a headline and list with dashes
				output += `${indent}${key}:\n`;
				// eslint-disable-next-line no-restricted-syntax
				for (const item of value) {
					output += `${indent} - ${item}\n`;
				}
			} else if (typeof value === 'object') {
				// For nested objects, apply recursion
				output += `${indent}${key}:\n`;
				output += printJsonAsPlainText(value, `${indent}  `);
			} else if (value) {
				// For simple types like strings and numbers, print the value without key
				output += `${indent}${key}: ${value}\n`;
			}
		}
		return output;
	};

	const copy = () => {
		navigator.clipboard
			.writeText(additionalContent ? shareUrl.urlAuthorized : shareUrl.urlNotAuthorized)
			.then(() => {
				setSuccess(2);
			})
			.catch(() => {
				setSuccess(1);
			});
	};

	const copyVehicleInformation = () => {
		const text = printJsonAsPlainText({ ...vehicle.data, technicalData: technicalData.data });
		navigator.clipboard
			.writeText(text)
			.then(() => {})
			.catch(() => {});
	};

	const share = () => {
		try {
			navigator
				.share({
					title: i18next.t('shareVehicle.subject' as any),
					text: i18next.t('shareVehicle.body' as any),
					url: additionalContent ? shareUrl.urlAuthorized : shareUrl.urlNotAuthorized,
				})
				.then(() => {
					setSuccess(2);
				})
				.catch(() => {
					window.location.href = `mailto:?subject=${i18next.t('shareVehicle.subject' as any)}!&body=${i18next.t('shareVehicle.body' as any)}%0D%0A${
						additionalContent ? shareUrl.urlAuthorized : shareUrl.urlNotAuthorized
					}`;
					setSuccess(2);
				});
		} catch (error) {
			console.log(error);
			window.location.href = `mailto:?subject=${i18next.t('shareVehicle.subject' as any)}!&body=${i18next.t('shareVehicle.body' as any)}%0D%0A${
				additionalContent ? shareUrl.urlAuthorized : shareUrl.urlNotAuthorized
			}`;
			setSuccess(2);
		}
	};

	const vinCheckTrackingButton = (which: 'copyLink' | 'share' | 'copyInfo') => {
		if (trackingService) {
			trackingService.track('VWBasic_Button_Click', {
				eventInfo: {
					eventAction: 'VWBasic_Button_Click',
					pageId: window.location.href,
					pageName: 'Vin Details',
					contentId: `vinDetails.shareVehicle.${which}`,
					sectionName: 'shareVehicle',
					brand: 'passenger',
				},
				user: {
					loginStatus: isLoggedIn,
					browserResolutionHeight: window.innerHeight,
					browserResolutionWidth: window.innerWidth,
					browserResolutionOrientation: window.innerHeight >= window.innerWidth ? 'v' : 'h',
				},
				personalization: {
					personalizable: false,
				},
				configuration: {
					CarlineName: vehicle.data.modelCarLine,
					SalesgroupName: vehicle.data.modelSalesGroup,
				},
				link: {
					name: which,
				},
			});
		}
	};

	useEffect(() => {
		const vinCheckTrackingLayerLoad = () => {
			if (trackingService) {
				trackingService.track('VWBasic_Layer_Load', {
					eventInfo: {
						eventAction: 'VWBasic_Layer_Load',
						pageId: window.location.href,
						pageName: 'Vin Details',
						contentId: 'vinDetails.shareVehicle',
						sectionName: 'shareVehicle',
						brand: 'passenger',
					},
					user: {
						loginStatus: isLoggedIn,
						browserResolutionHeight: window.innerHeight,
						browserResolutionWidth: window.innerWidth,
						browserResolutionOrientation: window.innerHeight >= window.innerWidth ? 'v' : 'h',
					},
					configuration: {
						CarlineName: vehicle.data.modelCarLine,
						SalesgroupName: vehicle.data.modelSalesGroup,
					},
				});
			}
		};

		vinCheckTrackingLayerLoad();
	}, []);

	// TODO request shareUrl from BE
	useEffect(() => {
		if ((shareUrl.urlAuthorized && shareUrl.urlNotAuthorized) || shareUrl.state === DataRequestState.Requested) {
			return;
		}

		const getShareUrlFunc = () => {
			// TODO 14122021 Handle error
			if (additionalContent && !shareUrl.urlAuthorized) {
				if (window.sessionStorage.getItem('shareUrlAuthorized')) {
					setShareUrl({ ...shareUrl, ...{ state: DataRequestState.Available, urlAuthorized: window.sessionStorage.getItem('shareUrlAuthorized')! } });
					setTempIsLoading(false);
					return;
				}

				setShareUrl({ ...shareUrl, ...{ state: DataRequestState.Requested } });
				setTempIsLoading(true);
				getShareUrl(additionalContent)
					.then((res) => {
						const url = new URL(historyService.history.createHref(`/share/${res}`), window.location.origin).href;
						setTempIsLoading(false);
						setShareUrl({ ...shareUrl, ...{ urlAuthorized: url, state: DataRequestState.Available } });
						window.sessionStorage.setItem('shareUrlAuthorized', url);
					})
					.catch(() => {
						setTempIsLoading(false);
						setShareUrl({ ...shareUrl, ...{ urlAuthorized: 'UNAUTHORIZED', state: DataRequestState.Available } });
					});
				return;
			}

			if (!additionalContent && !shareUrl.urlNotAuthorized) {
				if (window.sessionStorage.getItem('shareUrlNotAuthorized')) {
					setShareUrl({
						...shareUrl,
						...{ state: DataRequestState.Available, urlNotAuthorized: window.sessionStorage.getItem('shareUrlNotAuthorized')! },
					});
					setTempIsLoading(false);
					return;
				}

				setShareUrl({ ...shareUrl, ...{ state: DataRequestState.Requested } });
				setTempIsLoading(true);
				getShareUrl(additionalContent)
					.then((res) => {
						const url = new URL(historyService.history.createHref(`/share/${res}`), window.location.origin).href;
						setTempIsLoading(false);
						setShareUrl({ ...shareUrl, ...{ urlNotAuthorized: url, state: DataRequestState.Available } });
						window.sessionStorage.setItem('shareUrlNotAuthorized', url);
					})
					.catch(() => {
						setTempIsLoading(false);
						setShareUrl({ ...shareUrl, ...{ urlNotAuthorized: 'UNAUTHORIZED', state: DataRequestState.Available } });
					});
				return;
			}

			if (shareUrl.state !== DataRequestState.None) {
				setShareUrl({ ...shareUrl, ...{ state: DataRequestState.None } });
			}
		};

		getShareUrlFunc();
	}, [vin, additionalContent, getShareUrl, shareUrl]);

	return (
		<ThemeProvider theme="main">
			<Container wrap="always" gutter={ContainerGutter.dynamic0130} padding={{ left: ContainerPadding.grid002, right: ContainerPadding.grid002 }}>
				<Container gutter={ContainerGutter.dynamic0020} stretchContent>
					<Text appearance={TokenTextAppearance.headline400}>{t('shareVehicle.title')}</Text>

					<Text tag={TextTag.p} appearance={TokenTextAppearance.copy200}>
						{t('shareVehicle.content')}
					</Text>

					{/* <div className="share-link-area"> */}
					<div className="share-link">
						{tempIsLoading ? (
							<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
								<Spinner variant="large" />
							</div>
						) : (
							<TextInput
								value={additionalContent ? shareUrl.urlAuthorized : shareUrl.urlNotAuthorized}
								appearance={'readonly'}
								onFocus={(event) => event.currentTarget.select()}
								autoFocus={true}
							></TextInput>
						)}
					</div>
				</Container>

				<Container
					gutter={ContainerGutter.dynamic0050}
					stretchContent={{ [Breakpoints.default]: true, [Breakpoints.b560]: false }}
					wrap={{ [Breakpoints.default]: 'always', [Breakpoints.b960]: 'never' }}
					verticalAlign="center"
				>
					<div className="share-button-fixed-width">
						<CTA
							stretchContent
							tag="button"
							emphasis="secondary"
							onClick={() => {
								vinCheckTrackingButton('copyLink');
								copy();
							}}
						>
							{t('shareVehicle.copy')}
						</CTA>
					</div>

					<div className="share-button-fixed-width">
						<CTA
							stretchContent
							tag="button"
							onClick={() => {
								vinCheckTrackingButton('share');
								share();
							}}
						>
							{t('shareVehicle.send')}
						</CTA>
					</div>

					<Checkbox
						value="0"
						label={<Text tag={TextTag.span}>{t('shareVehicle.additionalContent')}</Text>}
						onChange={() => {
							setAdditionalContent(!additionalContent);
						}}
						disabled={!authorized}
					/>
				</Container>

				<Container wrap="always" gutter={ContainerGutter.dynamic0130}>
					<Container gutter={ContainerGutter.dynamic0020} stretchContent>
						<Text appearance={TokenTextAppearance.headline400}>{t('shareVehicle.copyClipboard.title')}</Text>

						<Text tag={TextTag.p}>{t('shareVehicle.copyClipboard.content')}</Text>
					</Container>

					<div className="share-button-fixed-width">
						<CTA
							stretchContent
							tag="button"
							emphasis="secondary"
							onClick={() => {
								vinCheckTrackingButton('copyInfo');
								copyVehicleInformation();
							}}
						>
							{t('shareVehicle.copyClipboard.copyButton')}
						</CTA>
					</div>
				</Container>
			</Container>
		</ThemeProvider>
	);
}

export default function renderShareVehicle(state: ShareVehicleProps, close: LayerCloseHandleV2<any>): JSX.Element {
	return (
		<Suspense fallback>
			<ShareVehicle state={state} close={close} />
		</Suspense>
	);
}

interface ShareVehicleProps {
	vin: string;
	historyService: HistoryServiceV3;
	getShareUrl: (withAdditionalContent: boolean) => Promise<'' | string>;
	authorized: boolean;
	trackingService: any;
	isLoggedIn: boolean;
	vehicle: VehicleObject;
	technicalData: TechnicalDataObject;
	tracking: {
		contentId: string;
		sectionName: string;
	};
	t: any;
}
